/**
 * Derived-data hooks.
 *
 * Thin, memoised selectors that run the ranking/analytics engines over store
 * state. Components consume these instead of recomputing, and memoisation keeps
 * the work off the render hot-path even with large datasets.
 */

import { useMemo } from "react";
import { useAppStore } from "@/store/useAppStore";
import {
  analytics as analyticsRows,
  recentlyUsed,
  topByUsage,
  trending,
} from "@/lib/engines/rankingEngine";
import { recommend, summarize } from "@/lib/engines/analyticsEngine";
import { sortFavorites } from "@/lib/engines/trackingEngine";
import type { Combination, Favorite } from "@/types";

export function useTopCombinations(): Combination[] {
  const combinations = useAppStore((s) => s.combinations);
  const topN = useAppStore((s) => s.config.topN);
  return useMemo(() => topByUsage(combinations, topN), [combinations, topN]);
}

export function useTrending() {
  const combinations = useAppStore((s) => s.combinations);
  const events = useAppStore((s) => s.events);
  const window = useAppStore((s) => s.config.defaultTrendingWindow);
  const topN = useAppStore((s) => s.config.topN);
  return useMemo(
    () => trending(combinations, events, window, topN),
    [combinations, events, window, topN],
  );
}

export function useRecent(n = 10): Combination[] {
  const combinations = useAppStore((s) => s.combinations);
  return useMemo(() => recentlyUsed(combinations, n), [combinations, n]);
}

export function useAnalyticsRows() {
  const combinations = useAppStore((s) => s.combinations);
  const events = useAppStore((s) => s.events);
  const window = useAppStore((s) => s.config.defaultTrendingWindow);
  const topN = useAppStore((s) => s.config.topN);
  return useMemo(
    () => analyticsRows(combinations, events, window, topN),
    [combinations, events, window, topN],
  );
}

export function useAnalyticsSummary() {
  const combinations = useAppStore((s) => s.combinations);
  const events = useAppStore((s) => s.events);
  const window = useAppStore((s) => s.config.defaultTrendingWindow);
  return useMemo(
    () => summarize(combinations, events, window),
    [combinations, events, window],
  );
}

export function useRecommendations() {
  const combinations = useAppStore((s) => s.combinations);
  const events = useAppStore((s) => s.events);
  const enabled = useAppStore((s) => s.config.enableAiRecommendations);
  const user = useAppStore((s) => s.tableau?.userName ?? "user");
  return useMemo(
    () => (enabled ? recommend(combinations, events, user, 5) : []),
    [combinations, events, enabled, user],
  );
}

/** Favorites joined to their combinations, sorted (pinned first). */
export function useFavoritesWithCombinations(): {
  favorite: Favorite;
  combination: Combination | undefined;
}[] {
  const favorites = useAppStore((s) => s.favorites);
  const combinations = useAppStore((s) => s.combinations);
  return useMemo(() => {
    const byId = new Map(combinations.map((c) => [c.id, c]));
    return sortFavorites(favorites).map((favorite) => ({
      favorite,
      combination: byId.get(favorite.combinationId),
    }));
  }, [favorites, combinations]);
}

/** The id of the combination matching the current draft, if any (for favoriting). */
export function useCurrentDraftCombination(): Combination | null {
  const draft = useAppStore((s) => s.draft);
  const descriptors = useAppStore((s) => s.descriptors);
  const combinations = useAppStore((s) => s.combinations);
  return useMemo(() => {
    // Lazily import to avoid a cycle; build assignments + hash inline.
    const assignments = descriptors
      .filter((d) => d.id in draft)
      .map((d) => ({
        parameterId: d.id,
        parameterName: d.name,
        dataType: d.dataType,
        value: draft[d.id],
        displayValue: String(draft[d.id] ?? ""),
      }));
    if (!assignments.length) return null;
    // Defer hashing to the engine via a dynamic require-free path.
    const sorted = [...assignments].sort((a, b) =>
      a.parameterId.localeCompare(b.parameterId),
    );
    const canonical = sorted.map((a) => `${a.parameterId}=${a.value ?? " "}`).join("&");
    const match = combinations.find((c) => {
      const cc = [...c.assignments]
        .sort((a, b) => a.parameterId.localeCompare(b.parameterId))
        .map((a) => `${a.parameterId}=${a.value ?? " "}`)
        .join("&");
      return cc === canonical;
    });
    return match ?? null;
  }, [draft, descriptors, combinations]);
}
